import { useState, useRef } from "react";
import "../styles/ProductCard.css";
import { BASE_URL } from "../config";
import { useApp } from "../App";
import { Link } from "react-router-dom";
import { useAuthGuard } from "../hooks/useAuthGuard";

const SAMPLE_PRODUCT = {
  id: "kkb-001",
  name: "Artisan Linen Throw Blanket",
  brand: "Maison Céleste",
  price: "3499",
  comparePrice: "4999",
  rating: 4.7,
  reviewCount: 128,
  inStock: true,
  image:
    "https://images.unsplash.com/photo-1580301762395-21ce84d00bc6?w=600&q=80",
  sizes: [
    { name: "Small (1.5m)", _id: "s1" },
    { name: "Regular (2.0m)", _id: "s2" },
    { name: "Large (2.5m)", _id: "s3" },
  ],
};

function StarRating({ rating }) {
  return (
    <div className="stars" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const fill = Math.min(1, Math.max(0, rating - (n - 1)));
        const id = `grad-${n}-${Math.random().toString(36).slice(2)}`;
        return (
          <svg
            key={n}
            viewBox="0 0 12 12"
            className="star-svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={id}>
                <stop offset={`${fill * 100}%`} stopColor="currentColor" />
                <stop offset={`${fill * 100}%`} stopColor="transparent" />
              </linearGradient>
            </defs>
            <polygon
              points="6,1 7.5,4.5 11.5,4.5 8.5,7 9.5,11 6,8.5 2.5,11 3.5,7 0.5,4.5 4.5,4.5"
              fill={`url(#${id})`}
              stroke="currentColor"
              strokeWidth="0.5"
            />
          </svg>
        );
      })}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="product-card skeleton-card" aria-hidden="true">
      <div className="pc-img-wrap">
        <div className="skeleton skeleton-img" />
      </div>
      <div className="pc-body">
        <div className="skeleton skeleton-brand" />
        <div className="skeleton skeleton-name" />
        <div className="skeleton skeleton-name short" />
        <div className="skeleton skeleton-price" />
      </div>
    </div>
  );
}

export default function ProductCard({
  product = SAMPLE_PRODUCT,
  isLoading = false,
}) {
  const availableSizes =
    typeof product.sizes === "string"
      ? JSON.parse(product.sizes)
      : product.sizes || [];
  const { addToCart, toggleWishlist, wishlist, showToast } = useApp(); // ✅ Add showToast here
  const { requireAuth } = useAuthGuard();
  const [selectedSize, setSelectedSize] = useState(
    availableSizes.length > 0 ? availableSizes[0].name : "",
  );
  const [addStatus, setAddStatus] = useState("idle");
  const [imgLoaded, setImgLoaded] = useState(false);
  // REMOVE this line:
  // const [isWished, setIsWished] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const cardRef = useRef(null);

  const discount = product.comparePrice
    ? Math.round(
        (1 - parseFloat(product.price) / parseFloat(product.comparePrice)) *
          100,
      )
    : null;

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  // USE THIS LINE instead:
  const isWished = wishlist.includes(product.id);

  const handleQuickAdd = (e) => {
    e.preventDefault();
    if (!product.inStock || addStatus !== "idle") return;

    // ✅ Check authentication before adding to cart
    requireAuth(() => {
      setAddStatus("loading");
      setTimeout(() => {
        addToCart(product, 1, selectedSize);
        setAddStatus("success");
        setTimeout(() => setAddStatus("idle"), 1500);
      }, 400);
    }, showToast);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    toggleWishlist(product.id);
  };

  if (isLoading) return <SkeletonCard />;

  return (
    <Link to={`/product/${product.id}`} className="product-card-img-wrap">
      <article
        className="product-card"
        ref={cardRef}
        onMouseMove={handleMouseMove}
        aria-label={`${product.name} by ${product.brand}`}
      >
        {/* ── Image Zone ── */}
        <div className="pc-img-wrap">
          <div
            className="img-spotlight"
            style={{
              background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, rgba(196,160,98,0.08) 0%, transparent 60%)`,
            }}
          />

          {!imgLoaded && (
            <div className="skeleton skeleton-img" aria-hidden="true" />
          )}
          <img
            src={`${BASE_URL}${product.image}`}
            alt={product.name}
            loading="lazy"
            className={`pc-img ${imgLoaded ? "loaded" : ""}`}
            onLoad={() => setImgLoaded(true)}
            onError={(e) => {
              e.target.src = `https://placehold.co/600x800/f0ece4/999?text=${encodeURIComponent(product.name?.substring(0, 10) || "Product")}`;
              setImgLoaded(true);
            }}
          />

          {/* Badges */}
          <div className="pc-badges" aria-label="Product labels">
            {discount >= 5 && (
              <span className="badge badge-sale" role="text">
                −{discount}%
              </span>
            )}
            {!product.inStock && (
              <span className="badge badge-sold" role="text">
                Sold Out
              </span>
            )}
          </div>

          {/* Wishlist */}
          <button
            className={`wishlist-btn ${isWished ? "active" : ""}`}
            onClick={handleWishlist}
            aria-label={isWished ? "Remove from wishlist" : "Add to wishlist"}
            aria-pressed={isWished}
          >
            <svg viewBox="0 0 24 24" className="heart-icon" aria-hidden="true">
              <path d="M12 21C12 21 3 14.5 3 8.5C3 5.42 5.42 3 8.5 3C10.24 3 11.81 3.85 12 5C12.19 3.85 13.76 3 15.5 3C18.58 3 21 5.42 21 8.5C21 14.5 12 21 12 21Z" />
            </svg>
          </button>

          {/* Quick View */}
          <button
            className="quick-view-btn"
            onClick={(e) => {
              e.preventDefault();
              setQuickViewOpen(true);
            }}
            aria-label="Quick view product details"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            Quick View
          </button>

          {/* Hover Overlay with Sizes + Add */}
          <div
            className="pc-overlay"
            role="group"
            aria-label="Quick add options"
          >
            {product.inStock && availableSizes.length > 0 && (
              <div className="size-zone">
                <p className="size-label" id="size-label">
                  Size
                </p>
                <div
                  className="size-chips"
                  role="radiogroup"
                  aria-labelledby="size-label"
                >
                  {availableSizes.map((s) => (
                    <button
                      key={s._id?.$oid || s.name}
                      role="radio"
                      aria-checked={selectedSize === s.name}
                      className={`size-chip ${selectedSize === s.name ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedSize(s.name);
                      }}
                    >
                      {s.name.split(" ")[0]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              className={`quick-add-btn status-${addStatus}`}
              disabled={!product.inStock || addStatus === "loading"}
              onClick={handleQuickAdd}
              aria-label={
                !product.inStock
                  ? "Product sold out"
                  : addStatus === "success"
                    ? "Added to cart"
                    : `Quick add ${product.name} to cart`
              }
              aria-live="polite"
            >
              <span className="btn-inner">
                {!product.inStock ? (
                  "Sold Out"
                ) : addStatus === "loading" ? (
                  <span className="spinner" aria-hidden="true" />
                ) : addStatus === "success" ? (
                  <>
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      aria-hidden="true"
                    >
                      <polyline points="2,8 6,12 14,4" />
                    </svg>
                    Added
                  </>
                ) : (
                  "Quick Add"
                )}
              </span>
            </button>
          </div>
        </div>

        {/* ── Card Body ── */}
        <div className="pc-body">
          {product.brand && (
            <p className="pc-brand" aria-label={`Brand: ${product.brand}`}>
              {product.brand}
            </p>
          )}

          <p className="pc-name">{product.name}</p>

          {/* {product.rating && (
          <div
            className="pc-rating"
            aria-label={`Rated ${product.rating} out of 5 from ${product.reviewCount} reviews`}
          >
            <StarRating rating={product.rating} />
            <span className="review-count">({product.reviewCount})</span>
          </div>
        )} */}

          <div className="pc-price">
            <span
              className="price-now"
              aria-label={`Price: ₹${parseFloat(product.price).toLocaleString("en-IN")}`}
            >
              ₹{parseFloat(product.price).toLocaleString("en-IN")}
            </span>
            {discount >= 5 && (
              <>
                <span
                  className="price-was"
                  aria-label={`Original price: ₹${parseFloat(product.comparePrice).toLocaleString("en-IN")}`}
                >
                  ₹{parseFloat(product.comparePrice).toLocaleString("en-IN")}
                </span>
                <span className="price-save" aria-label={`Save ${discount}%`}>
                  Save {discount}%
                </span>
              </>
            )}
          </div>
        </div>

        {/* Quick View Modal */}
        {quickViewOpen && (
          <div
            className="qv-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={`Quick view: ${product.name}`}
            onClick={() => setQuickViewOpen(false)}
          >
            <div className="qv-panel" onClick={(e) => e.stopPropagation()}>
              <button
                className="qv-close"
                onClick={() => setQuickViewOpen(false)}
                aria-label="Close quick view"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              {/* <div className="qv-img-wrap">
              <img src={`${BASE_URL}${product.image}`} alt={product.name} className="qv-img" />
            </div> */}
              <div className="qv-info">
                {product.brand && <p className="qv-brand">{product.brand}</p>}
                <h2 className="qv-name">{product.name}</h2>
                {/* {product.rating && (
                <div className="pc-rating qv-rating">
                  <StarRating rating={product.rating} />
                  <span className="review-count">
                    ({product.reviewCount} reviews)
                  </span>
                </div>
              )} */}
                <div className="pc-price qv-price">
                  <span className="price-now">
                    ₹{parseFloat(product.price).toLocaleString("en-IN")}
                  </span>
                  {discount >= 5 && (
                    <>
                      <span className="price-was">
                        ₹
                        {parseFloat(product.comparePrice).toLocaleString(
                          "en-IN",
                        )}
                      </span>
                      <span className="price-save">Save {discount}%</span>
                    </>
                  )}
                </div>
                {availableSizes.length > 0 && (
                  <div className="qv-sizes">
                    <p className="qv-size-label">Select Size</p>
                    <div className="size-chips qv-chips">
                      {availableSizes.map((s) => (
                        <button
                          key={s._id?.$oid || s.name}
                          className={`size-chip qv-size-chip ${selectedSize === s.name ? "active" : ""}`}
                          onClick={() => setSelectedSize(s.name)}
                          aria-pressed={selectedSize === s.name}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  className={`qv-add-btn status-${addStatus}`}
                  disabled={!product.inStock || addStatus === "loading"}
                  onClick={handleQuickAdd}
                >
                  {!product.inStock
                    ? "Sold Out"
                    : addStatus === "loading"
                      ? "Adding…"
                      : addStatus === "success"
                        ? "Added to Cart ✓"
                        : "Add to Cart"}
                </button>
              </div>
            </div>
          </div>
        )}
      </article>
    </Link>
  );
}
